import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { useTranslation } from 'react-i18next'

import {
  AccordionRoot,
  Box,
  type BoxProps,
  AccordionItem as EcAccordionItem,
  AccordionItemContent as EcAccordionItemContent,
  AccordionItemTrigger as EcAccordionItemTrigger,
  Flex,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import {
  FaClockRegular,
  FaHourglassEndRegular,
  FaInboxRegular,
} from '@enechain/ecloud-icons'

import { ExecutionAreaSummaryTable } from '~/components/bidding/common/ExecutionAreaSummaryTable'
import {
  computeExecutionAreaSummary,
  computeExecutionKpi,
} from '~/components/bidding/common/executionSummary'
import { AccordionDisclosureIcon } from '~/components/common/AccordionDisclosureIcon'
import { CompactAlert } from '~/components/common/CompactAlert'
import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { MultiSelect } from '~/components/common/MultiSelect'
import {
  FilterSection,
  FilterToolbar,
} from '~/components/common/filters/FilterToolbar'
import { usePersistedFilters } from '~/hooks/usePersistedFilters'
import { convertAreaLabel } from '~/models/area'
import { BlockExecutionTable } from '~/pages/SpotBidding/components/BlockExecutionTable'
import { SpotExecutionDetailTable } from '~/pages/SpotBidding/components/SpotExecutionDetailTable'
import { SpotExecutionKpiCards } from '~/pages/SpotBidding/components/SpotExecutionKpiCards'
import type {
  BlockBidRow,
  BlockExecKind,
} from '~/pages/SpotBidding/models/blockBidRow'
import { deriveBlockBidStatusCategory } from '~/pages/SpotBidding/models/blockBidStatusCategory'
import { convertBlockExecResults } from '~/pages/SpotBidding/models/convertBlockBidsFromApprovalRequests'
import {
  SPOT_EXECUTION_RESULT_FILTER_STORAGE_KEY,
  keepSelectableAreas,
  spotExecutionResultFilterCodec,
} from '~/pages/SpotBidding/models/executionResultFilters'
import { slotTimeLabel } from '~/pages/SpotBidding/models/komaOptions'
import {
  type SpotMarketPhase,
  isSpotBeforeDeadline,
} from '~/pages/SpotBidding/models/marketPhase'
import type { SpotBidRow } from '~/pages/SpotBidding/models/spotBidRow'
import {
  SPOT_BID_STATUS_FILTER_CATEGORIES,
  type SpotBidStatusCategory,
  deriveSpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'
import {
  type SpotExecutionResultFilter,
  filterBlockExecutionLegs,
  filterNormalExecutionBids,
} from '~/pages/SpotBidding/models/spotExecutionResult'
import {
  hasExecutionResult,
  toBlockExecutionSummaryRecords,
  toExecutionSummaryRecords,
} from '~/pages/SpotBidding/models/spotExecutionSummary'

// chakra v3.29 + React 18 types の既知不具合で、 Accordion item 系 component の props が
// `{ unstyled }` のみに collapse する (BlockBidTab と同じ cast)。
const AccordionItem = EcAccordionItem as React.FC<BoxProps & { value: string }>
const AccordionItemTrigger = EcAccordionItemTrigger as React.FC<BoxProps>
const AccordionItemContent = EcAccordionItemContent as React.FC<BoxProps>

type Props = {
  /** 受渡日の通常入札の札 (発注主体区分・共通ツールバーのエリアで絞らない)。 */
  bids: readonly SpotBidRow[]
  /** 受渡日のブロック入札の Leg (同上)。 */
  blockBidRows: readonly BlockBidRow[]
  /** エリアの選択肢 (= 業務エリア設定で許可された JEPX 取引対象エリア)。 */
  areaCodes: readonly AreaCode[]
  phase: SpotMarketPhase
  /** 札の取得が欠けている間は集計の数字を出さない (確定値として読ませない)。 */
  isIncomplete: boolean
}

// コマの選択肢は compact 形「{番号} ({開始時刻}〜{終了時刻})」 (AC-BSP-019 と同じ)。
const KOMA_FILTER_OPTIONS = Array.from({ length: 48 }, (_, i) => i + 1).map(
  (koma) => ({
    value: String(koma),
    label: `${koma} (${slotTimeLabel(koma)})`,
  }),
)

const SIDE_FILTER_VALUES = [Side.BUY, Side.SELL] as const

const BLOCK_EXEC_KIND_LABEL_KEY: Record<BlockExecKind, string> = {
  STANDARD: 'page:biddingSpot.blockExec.kind.standard',
  LINK: 'page:biddingSpot.blockExec.kind.link',
  LOOP: 'page:biddingSpot.blockExec.kind.loop',
}

/**
 * 約定結果タブ (AC-BSP-037)。
 *
 * 選択中の受渡日の約定実績を、 KPI カード (AC-BSP-002a)・エリア × 売買集計 (AC-BSP-002b)・
 * 約定明細 (通常入札。 列は AC-BSP-002)・約定結果 (ブロック入札) の 4 ブロックで出す。
 * KPI カードと集計は通常入札とブロック入札の約定を合わせる。 タブ内のフィルタは 4 ブロック
 * すべてに効き、 共通ツールバーのエリア・発注主体区分は使わない。
 *
 * 約定済フェーズより前 (約定処理中を含む) は案内と約定結果（ブロック入札）の空状態だけを出す
 * (design `renderResult` の `#spot-result-pre-deadline`。 KPI カードと集計は約定済フェーズ以降
 * AC-BSP-002a)。
 *
 * design: `design/bidding/spot.html` の `#tab-result-log`。
 */
export const ExecutionResultTab = ({
  bids,
  blockBidRows,
  areaCodes,
  phase,
  isIncomplete,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // タブ内のフィルタは AC-SC-031 に従い保持し、 タブの切り替え・他の画面への移動・再読み込みのあとに
  // 復元する (#12745)。 エリアは業務エリア外のものを表示と絞り込みの時だけ外す (保存値は変えない)。
  const { filters: savedFilter, updateFilters } =
    usePersistedFilters<SpotExecutionResultFilter>({
      storageKey: SPOT_EXECUTION_RESULT_FILTER_STORAGE_KEY,
      codec: spotExecutionResultFilterCodec,
    })

  const isPastDeadline = !isSpotBeforeDeadline(phase)
  const isPost = phase === 'matched' || phase === 'completed'

  const filter = useMemo<SpotExecutionResultFilter>(
    () => ({
      ...savedFilter,
      areas: keepSelectableAreas(savedFilter.areas, areaCodes),
    }),
    [savedFilter, areaCodes],
  )

  // 業務エリア外の札は出さない (`conventions.md`「業務エリアフィルタの原則」)。
  const allowedAreas = useMemo(() => new Set(areaCodes), [areaCodes])
  const normalRows = useMemo(
    () =>
      filterNormalExecutionBids(
        bids.filter((row) => allowedAreas.has(row.areaCode)),
        filter,
        isPastDeadline,
      ).sort((a, b) => a.timeCode - b.timeCode || a.areaCode - b.areaCode),
    [bids, allowedAreas, filter, isPastDeadline],
  )
  const blockLegs = useMemo(
    () =>
      filterBlockExecutionLegs(
        blockBidRows.filter((row) => allowedAreas.has(row.areaCode)),
        filter,
        isPastDeadline,
      ),
    [blockBidRows, allowedAreas, filter, isPastDeadline],
  )

  const normalRecords = useMemo(
    () => toExecutionSummaryRecords(normalRows, isPastDeadline),
    [normalRows, isPastDeadline],
  )
  const blockRecords = useMemo(
    () =>
      toBlockExecutionSummaryRecords(blockLegs, filter.komas, isPastDeadline),
    [blockLegs, filter.komas, isPastDeadline],
  )
  const kpi = useMemo(
    () => computeExecutionKpi([...normalRecords, ...blockRecords]),
    [normalRecords, blockRecords],
  )
  const blockKpi = useMemo(
    () => computeExecutionKpi(blockRecords),
    [blockRecords],
  )
  const areaSummary = useMemo(
    () => computeExecutionAreaSummary([...normalRecords, ...blockRecords]),
    [normalRecords, blockRecords],
  )

  // 表はグループ単位で出す。 約定結果が届いたグループ (全約定 / 未約定) のうち、 フィルタに合う
  // Leg を 1 本でも持つものは全 Leg を渡す (LOOP の A・B が別エリアでも片方が欠けないように)。
  const blockExecResults = useMemo(() => {
    const groupIds = new Set(
      blockLegs
        .filter((leg) =>
          hasExecutionResult(deriveBlockBidStatusCategory(leg, isPastDeadline)),
        )
        .map((leg) => leg.groupId),
    )
    return convertBlockExecResults(
      blockBidRows.filter((row) => groupIds.has(row.groupId)),
    )
  }, [blockBidRows, blockLegs, isPastDeadline])
  // 種別の内訳 (design `#block-exec-summary` の「(通常 N / リンク N / ループ N)」)。
  const blockBreakdown = (
    Object.entries(BLOCK_EXEC_KIND_LABEL_KEY) as Array<[BlockExecKind, string]>
  )
    .map(([kind, labelKey]) => ({
      label: t(labelKey),
      count: blockExecResults.filter((r) => r.kind === kind).length,
    }))
    .filter((x) => x.count > 0)
    .map((x) => `${x.label} ${x.count}`)
    .join(' / ')

  const statusCategoryOf = (row: SpotBidRow): SpotBidStatusCategory =>
    deriveSpotBidStatusCategory(row, isPastDeadline)

  // 約定結果（ブロック入札）の節は常に出し、 表を出せない時は理由を空状態で示す
  // (design `updateBlockExecutionVisibility`。 受付中 / 約定処理中 / ブロック入札が未提出)。
  const blockEmptyState = !isPastDeadline
    ? {
        icon: <FaClockRegular boxSize={EMPTY_PANEL_ICON_SIZE} />,
        key: 'beforeDeadline',
      }
    : !isPost
      ? {
          icon: <FaHourglassEndRegular boxSize={EMPTY_PANEL_ICON_SIZE} />,
          key: 'matching',
        }
      : blockBidRows.some((row) => allowedAreas.has(row.areaCode))
        ? undefined
        : {
            icon: <FaInboxRegular boxSize={EMPTY_PANEL_ICON_SIZE} />,
            key: 'noBlockBids',
          }
  const blockSection = (
    <ResultSection
      meta={
        blockEmptyState === undefined
          ? `${t('page:biddingSpot.executionResult.blockCount', {
              count: blockExecResults.length,
            })}${blockBreakdown !== '' ? ` (${blockBreakdown})` : ''}`
          : undefined
      }
      title={t('page:biddingSpot.blockExec.sectionTitle')}
      value="block"
    >
      {blockEmptyState === undefined ? (
        <BlockExecutionTable results={blockExecResults} />
      ) : (
        <EmptyPanel
          description={t(
            `page:biddingSpot.blockExec.emptyState.${blockEmptyState.key}.description`,
          )}
          icon={blockEmptyState.icon}
          title={t(
            `page:biddingSpot.blockExec.emptyState.${blockEmptyState.key}.title`,
          )}
        />
      )}
    </ResultSection>
  )

  if (!isPost) {
    return (
      <VStack align="stretch" gap={4}>
        {/* 受渡日が未到来という状態だけの表示 (操作を求めない) なので Alert でなく本文 (alert-placement.md §0) */}
        <Text color="Text.midEmphasis" fontSize="md">
          {t('page:biddingSpot.executionResult.beforeDeadline')}
        </Text>
        {blockSection}
      </VStack>
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      {/* フィルタはタブ全体 (KPI カード・集計・約定明細・約定結果（ブロック入札）) に効く
          (design `#spot-result-toolbar`)。 幅は design の値。 */}
      <FilterToolbar>
        <FilterSection
          label={t('page:biddingSpot.executionResult.filter.koma')}
        >
          <Box width="160px">
            <MultiSelect
              aria-label={t('page:biddingSpot.executionResult.filter.koma')}
              onChange={(next) =>
                updateFilters((prev) => ({
                  ...prev,
                  komas: new Set(next.map(Number)),
                }))
              }
              options={KOMA_FILTER_OPTIONS}
              placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
              selected={Array.from(filter.komas, String)}
            />
          </Box>
        </FilterSection>
        <FilterSection
          label={t('page:biddingSpot.executionResult.filter.area')}
        >
          <Box width="140px">
            <MultiSelect
              aria-label={t('page:biddingSpot.executionResult.filter.area')}
              onChange={(next) =>
                updateFilters((prev) => ({
                  ...prev,
                  areas: new Set(next.map((v) => Number(v) as AreaCode)),
                }))
              }
              options={areaCodes.map((code) => ({
                value: String(code),
                label: convertAreaLabel(code),
              }))}
              placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
              selected={Array.from(filter.areas, String)}
            />
          </Box>
        </FilterSection>
        <FilterSection label={t('page:biddingSpot.sidebar.filterSide')}>
          <Box width="110px">
            <MultiSelect
              aria-label={t('page:biddingSpot.sidebar.filterSide')}
              onChange={(next) =>
                updateFilters((prev) => ({
                  ...prev,
                  sides: new Set(next.map((v) => Number(v) as Side)),
                }))
              }
              options={SIDE_FILTER_VALUES.map((side) => ({
                value: String(side),
                label: t(
                  side === Side.BUY
                    ? 'page:biddingSpot.side.buy'
                    : 'page:biddingSpot.side.sell',
                ),
              }))}
              placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
              selected={Array.from(filter.sides, String)}
            />
          </Box>
        </FilterSection>
        <FilterSection label={t('page:biddingSpot.sidebar.filterStatus')}>
          <Box width="130px">
            <MultiSelect
              aria-label={t('page:biddingSpot.sidebar.filterStatus')}
              onChange={(next) =>
                updateFilters((prev) => ({
                  ...prev,
                  statuses: new Set(next as SpotBidStatusCategory[]),
                }))
              }
              options={SPOT_BID_STATUS_FILTER_CATEGORIES.map((category) => ({
                value: category,
                label: t(`page:biddingSpot.bidStatusCategory.${category}`),
              }))}
              placeholder={t('page:biddingSpot.sidebar.filterStatusAll')}
              selected={Array.from(filter.statuses)}
            />
          </Box>
        </FilterSection>
      </FilterToolbar>

      {isIncomplete ? (
        <CompactAlert
          description={t('page:biddingSpot.execSummary.incompleteBody')}
          status="warning"
          title={t('page:biddingSpot.execSummary.incompleteTitle')}
        />
      ) : (
        <>
          <Text color="Text.midEmphasis" fontSize="md">
            {t('page:biddingSpot.executionResult.summaryNote')}
          </Text>
          <SpotExecutionKpiCards
            blockExecVolumeMw={{
              buy: blockKpi.buyExecVolumeMw,
              sell: blockKpi.sellExecVolumeMw,
            }}
            kpi={kpi}
          />
          <ResultSection
            title={t('page:biddingSpot.execSummary.title')}
            value="areaSummary"
          >
            <Box overflowX="auto">
              <ExecutionAreaSummaryTable
                rows={areaSummary.rows}
                total={areaSummary.total}
              />
            </Box>
          </ResultSection>
        </>
      )}

      <ResultSection
        description={t('page:biddingSpot.executionResult.detail.description')}
        meta={t('page:biddingSpot.executionResult.count', {
          count: normalRows.length,
        })}
        title={t('page:biddingSpot.executionResult.detail.title')}
        value="detail"
      >
        <SpotExecutionDetailTable
          rows={normalRows}
          statusCategoryOf={statusCategoryOf}
        />
      </ResultSection>

      {blockSection}
    </VStack>
  )
}

/** 見出し付きの開閉カード (design `.ecl-accordion`、 既定で開く)。 */
const ResultSection = ({
  title,
  meta,
  description,
  value,
  children,
}: {
  title: string
  meta?: string
  description?: string
  value: string
  children: React.ReactNode
}): React.ReactElement => (
  <AccordionRoot collapsible defaultValue={[value]}>
    <AccordionItem
      overflow="hidden"
      borderWidth="1px"
      borderColor="Border.lowEmphasis"
      borderRadius="lg"
      backgroundColor="Surface.primary"
      value={value}
    >
      <AccordionItemTrigger paddingX={4} paddingY={3}>
        <AccordionDisclosureIcon />
        <Flex alignItems="center" flexWrap="wrap" flex={1} gap={3}>
          <Text textStyle="sectionTitle" flexShrink={0}>
            {title}
            {meta !== undefined && (
              <Text
                as="span"
                textStyle="captionNormal"
                marginLeft={2}
                color="Text.midEmphasis"
                css={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {meta}
              </Text>
            )}
          </Text>
          {description !== undefined && (
            <Text color="Text.midEmphasis" fontSize="sm">
              {description}
            </Text>
          )}
        </Flex>
      </AccordionItemTrigger>
      <AccordionItemContent
        padding={0}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        {children}
      </AccordionItemContent>
    </AccordionItem>
  </AccordionRoot>
)
